import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import { MatPaginator, MatSort } from "@angular/material";
import { fromEvent, merge, of as observableOf, Subject } from "rxjs";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  startWith,
  switchMap,
  takeUntil,
} from "rxjs/operators";
import { SelectionModel } from "@angular/cdk/collections";
import { FormBuilder, FormGroup } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Player, PlayerApi } from "@lbsdk/*";
import { FuseSplashScreenService } from "@fuse/services/splash-screen.service";
import { GameSettingsService } from "@app/main/content/game-settings/game-settings.service";

@Component({
  selector: "app-player-listing",
  templateUrl: "./player-listing.component.html",
  styleUrls: ["./player-listing.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class PlayerListingComponent implements AfterViewInit {
  displayedColumns: string[] = ["select", "username", "email"];
  exampleDatabase: ExampleHttpDatabase | null;
  data: any[] = [];
  isRateLimitReached = false;
  selection = new SelectionModel<Player>(true, []);
  dialogTitle: string;
  // selectedPlayers: any[] = [];
  prizeForm: FormGroup;
  action: string;
  gameId: any;
  restart: any;
  // Private
  private _unsubscribeAll: Subject<any>;

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild("filter")
  filter: ElementRef;

  constructor(
    public dialogRef: MatDialogRef<PlayerListingComponent>,
    @Inject(MAT_DIALOG_DATA) private dialogData: any,
    private _fuseSplashScreenService: FuseSplashScreenService,
    public _gameSettingsService: GameSettingsService,
    private playerApi: PlayerApi,
    private formBuilder: FormBuilder
  ) {
    // Set the private defaults
    this._unsubscribeAll = new Subject();

    this.action = dialogData.action;

    if (this.action === "edit") {
      this.dialogTitle = "Players";
      this._gameSettingsService.selectedPlayers =
        [...dialogData.selectedPlayers.map((el) => (el.id ? el.id : el))];
      this.selection.select(...dialogData.selectedPlayers);
      this.gameId = dialogData.gameId;
      this.restart = dialogData.restart;
    } else {
      this.dialogTitle = "Players";
    }
  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.data.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    if (this.isAllSelected()) {
      this.selection.clear();
      this._gameSettingsService.selectedPlayers = [];
    } else {
      this.data.forEach((row) => this.selection.select(row));
    }
    // this.isAllSelected() ?
    //   this.selection.clear() :
    //   this.data.forEach(row => this.selection.select(row));
  }

  isSelectedPlayer(item): boolean {
    return (
      this._gameSettingsService.selectedPlayers.filter(
        (playerId) => playerId === item.id
      ).length !== 0
    );
  }

  isSelected(row) {
    return (
      this.selection.selected.filter((item) => item.id === row.id).length !== 0
    );
  }

  ngAfterViewInit() {
    this.exampleDatabase = new ExampleHttpDatabase(this.playerApi);

    // If the user changes the sort order, reset back to the first page.
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    merge(this.sort.sortChange, this.paginator.page)
      .pipe(
        startWith({}),
        switchMap(() => {
          this._fuseSplashScreenService.show();
          return this.exampleDatabase!.getData(
            "createdAt DESC",
            this.paginator
          );
        }),
        map((data) => {
          // Flip flag to show that loading has finished.
          this._fuseSplashScreenService.hide();
          this.isRateLimitReached = false;
          return data;
        }),
        catchError(() => {
          this._fuseSplashScreenService.hide();
          // Catch if the GitHub API has reached its rate limit. Return empty data.
          this.isRateLimitReached = true;
          return observableOf([]);
        })
      )
      .subscribe((data) => {
        this.data = data;
      });
    this.applyFilter();
  }

  applyFilter() {
    fromEvent(this.filter.nativeElement, "keyup")
      .pipe(
        takeUntil(this._unsubscribeAll),
        debounceTime(150),
        distinctUntilChanged()
      )
      .subscribe(() => {
        if (!this.data) {
          return;
        }
        merge(this.sort.sortChange, this.paginator.page)
          .pipe(
            startWith({}),
            switchMap(() => {
              this._fuseSplashScreenService.show();
              return this.exampleDatabase!.getData(
                "createdAt DESC",
                this.paginator,
                this.filter.nativeElement.value
              );
            }),
            map((data) => {
              // Flip flag to show that loading has finished.
              this._fuseSplashScreenService.hide();
              this.isRateLimitReached = false;
              return data;
            }),
            catchError(() => {
              this._fuseSplashScreenService.hide();
              // Catch if the GitHub API has reached its rate limit. Return empty data.
              this.isRateLimitReached = true;
              return observableOf([]);
            })
          )
          .subscribe((data) => (this.data = data));
      });
  }

  onClickRow($event, player) {
    this.selection.toggle(player);
    if (
      this._gameSettingsService.selectedPlayers.filter((el) => el === player.id)
        .length === 0
    ) {
      this._gameSettingsService.selectedPlayers.push(player.id);
    } else {
      this._gameSettingsService.selectedPlayers =
        this._gameSettingsService.selectedPlayers.filter(
          (el) => el !== player.id
        );
    }
  }
}

/** An example database that the data source uses to retrieve data for the table. */
export class ExampleHttpDatabase {
  constructor(private _playerApi: PlayerApi) {}

  getData(order?, page?, filter?): Promise<any> {
    if (!page.pageSize) {
      page.pageSize = 10;
    }
    if (!filter) {
      return new Promise((resolve, reject) => {
        this._playerApi
          .find({
            limit: page.pageSize,
            skip: page.pageIndex * page.pageSize,
          })
          .subscribe(
            (response: any[]) => {
              resolve(response);
            },
            () => {
              // Show the success message
              alert("Internal Server Error!");
              reject();
            }
          );
      });
    }
    return new Promise((resolve, reject) => {
      this._playerApi
        .find({
          where: {
            or: [
              { username: { like: filter, options: "i" } },
              { email: { like: filter, options: "i" } },
            ],
          },
          limit: page.pageSize,
          skip: page.pageIndex * page.pageSize,
          order: order,
        })
        .subscribe(
          (response: any[]) => {
            resolve(response);
          },
          () => {
            // Show the success message
            alert("Internal Server Error!");
            reject();
          }
        );
    });
  }
}